/**
 * One Idempotency-Key per user action (server/middleware/idempotency.js).
 *
 * When a reply is lost, the server may already have done the work. Retrying
 * the same unconfirmed action with the SAME key gets the original result back
 * instead of a second message, proposal or offer (break test B-16, B-18, B-33).
 * Anything else gets a new key.
 *
 * `slot` names the draft the action comes from (the chat box, the offer being
 * built, one trade's meetup suggestion) and `sig` is what it sends. Only the
 * latest unconfirmed action per slot is kept, so the same words sent again
 * after a different message are a new message.
 *
 * A key is only right for a RETRY: the same draft, sent again, with nothing
 * else having happened to it. The server replays a stored reply for 24 hours
 * without looking at the body, so a key kept past that point turns a new
 * message, offer or suggestion with the same content into a silent replay of
 * the old one. The screen that owns the draft therefore calls `forgetAction`
 * as soon as the draft is no longer the one that failed: the chat box is
 * edited, the offer builder is reset, or the other trader suggests somewhere
 * else or a meetup is agreed (useMeetupRetryReset).
 *
 * Written without async/await or optional chaining on purpose: both compile to
 * a lot of code for the browsers this bundle targets, and the main chunk is
 * near its size budget.
 */
import { randomId } from "./randomId";

const unconfirmed = new Map();

export const sendAsOneAction = (slot, sig, send) => {
  const last = unconfirmed.get(slot);
  // randomId falls back where crypto.randomUUID is missing (older WebViews).
  const key = last && last.sig === sig ? last.key : randomId();
  unconfirmed.set(slot, { sig, key });
  return Promise.resolve(send({ "Idempotency-Key": key })).then((result) => {
    const now = unconfirmed.get(slot);
    if (now && now.key === key) unconfirmed.delete(slot);
    return result;
  });
};

// The draft in `slot` is gone or has moved on, so its next send is a new action.
export const forgetAction = (slot) => {
  unconfirmed.delete(slot);
};
